// Human identity. Keycloak (customers realm) authenticates; gcstore authorises. This
// module knows only the first half.
//
// Two strategies, chosen by build config:
//
//   oidc  — the real thing: auth-code + PKCE against the customers realm.
//   mock  — development, before the Keycloak client exists. Names one of the mock
//           gcstore's scripted subjects instead of proving anything.
//
// The mock strategy exists so the player, the renewal loop and every lock screen can be
// built and exercised before anyone provisions a realm client. It cannot be reached in a
// production build: VITE_IDENTITY_MODE is baked in at build time and the deploy sets it to
// `oidc`.

// Imported statically, not lazily.
//
// It was behind a dynamic import back when the catalogue was public and most visitors never
// needed it. Now that every route is gated, this library is required before the first pixel
// renders, so deferring it only adds a network round trip to the "Verificando acesso…"
// screen — which is exactly the delay that was reported.
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'

const MODE = (import.meta.env.VITE_IDENTITY_MODE ?? 'mock') as 'oidc' | 'mock'

export interface Identity {
  /** A bearer token for gcstore, or null in mock mode. */
  bearer(): Promise<string | null>
  /** Which scripted subject to ask the mock for; null in oidc mode. */
  mockSubject(): string | null
  /**
   * True when this strategy needs a bearer and does not currently have one, i.e. the
   * viewer must be sent to Keycloak before gcstore is worth calling.
   *
   * Always false in mock mode, where having no bearer is the correct steady state.
   */
  needsLogin(): Promise<boolean>
  /** Redirects to Keycloak. `returnTo` is restored after the callback. */
  login(returnTo?: string): Promise<void>
  logout(): Promise<void>
  /** Completes the callback and returns the route to resume, if one was stored. */
  completeLoginRedirect(): Promise<string | null>
  displayName(): string | null
  isMock: boolean
}

const RETURN_TO_KEY = 'gcflix.returnTo'
const ATTEMPT_KEY = 'gcflix.loginAttemptAt'

/**
 * How recently a login attempt counts as "we just did this".
 *
 * Redirecting to an identity provider is the one action that can silently loop forever:
 * every hop looks like progress, the screen never settles, and there is no error anywhere.
 * If a fresh login lands us straight back at "you need to log in", something other than the
 * session is wrong and we must say so instead of bouncing again.
 */
const LOOP_WINDOW_MS = 30_000

export class LoginLoopError extends Error {
  constructor() {
    super('login_loop')
    this.name = 'LoginLoopError'
  }
}

class MockIdentity implements Identity {
  isMock = true
  private sub: string

  constructor() {
    // ?sub= wins so a tester can flip between entitled, locked, expired and revoked
    // without rebuilding — every lock screen is one URL away.
    const fromUrl = new URLSearchParams(window.location.search).get('sub')
    this.sub = fromUrl ?? localStorage.getItem('gcflix.mockSub') ?? 'dev-entitled'
    localStorage.setItem('gcflix.mockSub', this.sub)
  }

  async bearer() {
    return null
  }
  mockSubject() {
    return this.sub
  }
  async needsLogin() {
    return false
  }
  async login() {}
  async logout() {
    localStorage.removeItem('gcflix.mockSub')
  }
  async completeLoginRedirect() {
    return null
  }
  displayName() {
    return this.sub
  }
}

class OidcIdentity implements Identity {
  isMock = false
  private mgr: UserManager
  private user: User | null = null

  constructor() {
    this.mgr = new UserManager({
      authority: import.meta.env.VITE_KC_ISSUER,
      client_id: import.meta.env.VITE_KC_CLIENT_ID,
      redirect_uri: window.location.origin + '/auth/callback',
      post_logout_redirect_uri: window.location.origin + '/',
      response_type: 'code', // auth-code + PKCE; oidc-client-ts enables PKCE by default
      scope: 'openid profile',
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: true,
    })
  }

  private async manager(): Promise<UserManager> {
    return this.mgr
  }

  /**
   * Returns a live access token, refreshing silently when it has expired.
   *
   * A stale Keycloak token must never surface as a login prompt mid-lecture: the renewal
   * loop calls this every time it goes back to gcstore, and the whole point is that the
   * viewer never notices.
   */
  async bearer() {
    const mgr = await this.manager()
    this.user = await mgr.getUser()
    if (!this.user || this.user.expired) {
      try {
        this.user = await mgr.signinSilent()
      } catch {
        return null
      }
    }
    return this.user?.access_token ?? null
  }

  mockSubject() {
    return null
  }

  async needsLogin() {
    return (await this.bearer()) === null
  }

  /**
   * Sends the viewer to Keycloak, remembering where they were.
   *
   * The return route is stored before the redirect because the round trip leaves the app
   * entirely: without it, someone who clicks play on a lecture comes back to the catalogue
   * and has to find it again.
   */
  async login(returnTo?: string) {
    const last = Number(sessionStorage.getItem(ATTEMPT_KEY) ?? 0)
    if (last && Date.now() - last < LOOP_WINDOW_MS) {
      sessionStorage.removeItem(ATTEMPT_KEY)
      throw new LoginLoopError()
    }
    sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()))
    if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo)
    await (await this.manager()).signinRedirect()
  }

  async logout() {
    await (await this.manager()).signoutRedirect()
  }

  async completeLoginRedirect() {
    this.user = await (await this.manager()).signinRedirectCallback()
    const back = sessionStorage.getItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RETURN_TO_KEY)
    // Deliberately NOT clearing ATTEMPT_KEY: a successful callback that still leaves us
    // without a usable session is exactly the loop this guards against, and clearing here
    // would hide it. It ages out on its own.
    return back
  }
  displayName() {
    return (this.user?.profile?.name as string) ?? (this.user?.profile?.preferred_username as string) ?? null
  }
}

export const identity: Identity = MODE === 'oidc' ? new OidcIdentity() : new MockIdentity()
export const identityMode = MODE
